import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { useTherapist } from "../../../context/useTherapist";
import { EmdrSessionsAPI } from "../infrastructure/api/EmdrSessions.api";
import { boardPath } from "../domain/boardPaths";

/** Who the new session is with. */
export type EmdrPatient = { id: string; name: string };

/**
 * Starts EMDR from a dock: a new in-person session with the patient, and the
 * board for it in a new tab.
 *
 * Always a new session, never one already on the calendar: the sets rated on
 * the board — and the note written from them — belong to the appointment
 * happening now. Opening the board for an existing session is done from that
 * session.
 *
 * The tab is opened before the session is created, while the click still
 * counts as the therapist's: a tab opened after the request comes back is a
 * popup to the browser, and phones block it.
 */
export function useStartEmdrSession() {
    const { t } = useTranslation();
    const { therapist } = useTherapist();
    const [starting, setStarting] = useState(false);
    const [failed, setFailed] = useState(false);

    const start = useCallback(
        async (patient: EmdrPatient): Promise<boolean> => {
            if (starting || !therapist?.id) return false;
            setStarting(true);
            setFailed(false);
            const tab = window.open("", "_blank");
            try {
                const now = new Date();
                const date = now.toLocaleDateString("en-CA"); // YYYY-MM-DD, local
                const hour = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
                const { data } = await EmdrSessionsAPI.create({
                    title: t("emdr.dock.sessionTitle", { date, name: patient.name }),
                    date,
                    hour,
                    patient_id: patient.id,
                    therapist_id: therapist.id,
                    therapy_type: "emdr",
                    type: "individual",
                    ubication: "in person",
                    duration: 50,
                    notes: "",
                });
                const path = boardPath(data.id);
                if (tab) {
                    tab.opener = null;
                    tab.location.href = path;
                } else {
                    // The browser refused the tab: the board in this one.
                    window.location.assign(path);
                }
                return true;
            } catch {
                tab?.close();
                setFailed(true);
                return false;
            } finally {
                setStarting(false);
            }
        },
        [starting, therapist?.id, t]
    );

    return { start, starting, failed };
}
